import { lazy, Suspense } from "react";
import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
} from "react-router-dom";
import styled from "styled-components";
import RouteComponent from "./components/RouteComponent";

import AppNavigation from "./components/AppNavigation";
import Shell from "./components/Shell";
import PositionedSpinner from "./components/Spinner";
import RequireAuth from "./auth/RequireAuth";
import { isCommercialEnvironment } from "./utils/environment";
import { FileRequestSourceType } from "./api/endpoints/sources/subpoenaApiTypes";

const Unauthorized = lazy(() => import("./pages/error/Unauthorized.page"));
const NotFound = lazy(() => import("./pages/error/NotFound.page"));
const ErrorPage = lazy(() => import("./pages/error/Error.page"));

const CaseList = lazy(() => import("./pages/case-list/CaseList.page"));
const AddSource = lazy(() => import("./pages/add-source-view/AddSource.page"));
const ColumnFormattingErrors = lazy(
  () => import("./pages/add-source-view/ColumnFormattingErrors.page")
);
const AddSourceLanding = lazy(
  () => import("./pages/add-source-view/AddSourceLanding.page")
);

const ReportList = lazy(() => import("./pages/report-list/ReportList.page"));
const CaseSettingView = lazy(
  () => import("./pages/case-setting-view/CaseSetting.page")
);
const ResolutionCenter = lazy(
  () => import("./pages/resolution-center-view/ResolutionCenter.page")
);
const SourceList = lazy(() => import("./pages/source-view/Source.page"));
const TransactionDetails = lazy(
  () => import("./pages/transaction-detail-view/TransactionDetails.page")
);
const GenericCsv = lazy(() => import("./pages/add-source-view/GenericCsv"));
const JsonLFile = lazy(() => import("./pages/add-source-view/JsonLFile"));
const PublicAddress = lazy(
  () => import("./pages/add-source-view/PublicAddress")
);
const CaseIndexPage = lazy(() => import("./pages/case-default/CaseIndexPage"));
const TransactionList = lazy(
  () => import("./pages/transaction-list/TransactionList.page")
);
const SourceDetails = lazy(
  () => import("./pages/source-detail-view/SourceDetails.page")
);
const SourceOption = lazy(() => import("./pages/source-view/SourceOption"));

const router = createBrowserRouter([
  {
    path: "unauthorized",
    element: (
      <RouteComponent>
        <Unauthorized />
      </RouteComponent>
    ),
  },
  {
    path: "/",
    element: (
      <RequireAuth>
        <Shell />
      </RequireAuth>
    ),
    errorElement: (
      <RouteComponent>
        <ErrorPage />
      </RouteComponent>
    ),
    children: [
      {
        index: true,
        element: <Navigate to="case-list" />,
        errorElement: (
          <RouteComponent>
            <ErrorPage />
          </RouteComponent>
        ),
      },
      {
        path: "case-list",
        element: <CaseList />,
        errorElement: (
          <RouteComponent>
            <ErrorPage />
          </RouteComponent>
        ),
      },
      {
        path: "case-list/:caseId",
        element: <AppNavigation />,
        errorElement: (
          <RouteComponent>
            <ErrorPage />
          </RouteComponent>
        ),
        children: [
          {
            index: true,
            element: (
              <RouteComponent>
                <CaseIndexPage />
              </RouteComponent>
            ),
          },
          {
            path: "report-list",
            element: (
              <RouteComponent>
                <ReportList />
              </RouteComponent>
            ),
          },
          {
            path: "transactions",
            element: (
              <RouteComponent>
                <TransactionList />
              </RouteComponent>
            ),
          },
          {
            path: "transactions/:transactionId",
            element: (
              <RouteComponent>
                <TransactionDetails />
              </RouteComponent>
            ),
          },
          {
            path: "sources",
            element: (
              <RouteComponent>
                <SourceList />
              </RouteComponent>
            ),
            children: [
              {
                index: true,
                path: "generic-csv",
                element: (
                  <RouteComponent>
                    <SourceOption
                      sourceType={FileRequestSourceType.SUBPOENA_INTERNAL}
                    />
                  </RouteComponent>
                ),
              },
              {
                path: "jsonl",
                element: (
                  <RouteComponent>
                    <SourceOption sourceType={FileRequestSourceType.JSONL} />
                  </RouteComponent>
                ),
              },
              ...(isCommercialEnvironment()
                ? [
                    {
                      path: "public-address",
                      element: (
                        <RouteComponent>
                          <SourceOption
                            sourceType={FileRequestSourceType.WALLET}
                          />
                        </RouteComponent>
                      ),
                    },
                  ]
                : []),
            ],
          },
          {
            path: "sources/:sourceId",
            element: (
              <RouteComponent>
                <SourceDetails />
              </RouteComponent>
            ),
          },
          {
            path: "sources",
            element: (
              <RouteComponent>
                <AddSourceLanding />
              </RouteComponent>
            ),
            children: [
              {
                path: "add",
                element: (
                  <RouteComponent>
                    <AddSource />
                  </RouteComponent>
                ),
                children: [
                  {
                    index: true,
                    path: "generic-csv",
                    element: (
                      <RouteComponent>
                        <GenericCsv />
                      </RouteComponent>
                    ),
                  },
                  {
                    path: "jsonl",
                    element: (
                      <RouteComponent>
                        <JsonLFile />
                      </RouteComponent>
                    ),
                  },
                  ...(isCommercialEnvironment()
                    ? [
                        {
                          path: "public-address",
                          element: (
                            <RouteComponent>
                              <PublicAddress />
                            </RouteComponent>
                          ),
                        },
                      ]
                    : []),
                ],
              },
              {
                path: "column-formatting-errors",
                element: (
                  <RouteComponent>
                    <ColumnFormattingErrors />
                  </RouteComponent>
                ),
              },
            ],
          },
          {
            path: "resolution-center",
            element: (
              <RouteComponent>
                <ResolutionCenter />
              </RouteComponent>
            ),
          },
          {
            path: "case-settings",
            element: (
              <RouteComponent>
                <CaseSettingView />
              </RouteComponent>
            ),
          },
        ],
      },
      {
        path: "*",
        element: (
          <RouteComponent>
            <NotFound />
          </RouteComponent>
        ),
        errorElement: (
          <RouteComponent>
            <ErrorPage />
          </RouteComponent>
        ),
      },
    ],
  },
]);

export const App = () => (
  <AppWrapper>
    <Suspense fallback={<PositionedSpinner />}>
      <RouterProvider router={router} />
    </Suspense>
  </AppWrapper>
);

const AppWrapper = styled.div(
  ({ theme }) => `
  background-color: ${theme.color.gray0};
  height: 100vh;
  width:100vw;
  overflow: auto;
`
);
